import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  bookManualDelivery,
  createOwnDriverDelivery,
  quoteAllOptions,
  retryManualDeliveryBooking,
} from "util/manualDeliveries";
import {
  bookingOutcome,
  bookingRequest,
  canBook,
  footerModel,
  initialPanelState,
  offerFromOption,
  panelReducer,
  REVIEW_MIN_SECONDS,
  reviewAfterRefresh,
  reviewWait,
  secondsLeft,
  successToast,
} from "util/manualDeliveryBooking";
import {
  applyFix,
  blankForm,
  firstErrorField,
  isFormDirty,
  ownDriverPayload,
  shipmentFromForm,
  shipmentKey,
  validateForm,
  withDate,
  withOpenStart,
} from "util/manualDeliveryForm";
import { dayLabel, shopNow, windowText } from "util/manualDeliveryTime";
import CarrierOptions, { OWN_FALLBACK } from "./CarrierOptions";
import { useConfirm } from "./ConfirmDialog";
import DeliveryForm from "./DeliveryForm";
import PanelFooter from "./PanelFooter";
import ReviewStep from "./ReviewStep";
import useFocusTrap from "./useFocusTrap";
import useQuotes, { QUOTE_DEBOUNCE_MS } from "./useQuotes";
import "../ManualDeliveries.css";

// Phases where the server may hold a row or a booking: closing reports it instead of asking to discard.
const NO_DISCARD = ["booking", "unknown", "done"];

// The New delivery side panel: step 1 (details and live prices for every option), step 2 (review and book), and
// every booking outcome in the footer. seed is a "Copy as new delivery" prefill (formFromRow) or null.
// onBooked(toast, row) after a booking or own-driver save. onSaved(kind, rowId) when it closes after a booking
// problem: "notBooked" (the server kept a row that is not booked), "unknown" (a kept row the courier may hold),
// "unconfirmed" (the courier may hold a booking and no row came back) or "ownUnconfirmed" (an own-driver save that
// may have happened); rowId is null when no row came back. onClose() whenever it closes.
// Before the first Review only a passed start is flagged (it can pass while the panel is open): it stops the prices
// loading, so it must say why at once.
const startError = (errors) => (errors.start ? { start: errors.start } : {});

export default function NewDeliveryPanel({
  shopId,
  timezone,
  todayIso,
  seed,
  onClose,
  onBooked,
  onSaved,
  quoteDelayMs = QUOTE_DEBOUNCE_MS,
}) {
  // A start that has already passed is moved before the panel opens, so the form opens untouched and quotable.
  const [base] = useState(() => withOpenStart(seed || blankForm(todayIso), shopNow(timezone, new Date(Date.now()))));
  const [form, setForm] = useState(base);
  const [state, dispatch] = useReducer(panelReducer, initialPanelState);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  // Review pressed on a price about to lapse: the fresh price it waits on (reviewWait), and whether that price came
  // back different, so staff look at it before pressing Review again.
  const [waiting, setWaiting] = useState(null);
  const [refreshed, setRefreshed] = useState(false);
  const [confirmElement, confirm] = useConfirm();
  // Blocks a second booking request before React has re-rendered the disabled button.
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Wraps the overlay and the panel: the trap makes everything outside it inert, and the overlay must stay
  // clickable.
  const layerRef = useRef(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const carriersRef = useRef(null);
  useFocusTrap(layerRef, true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (titleRef.current) titleRef.current.focus();
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [state.step]);

  const now = shopNow(timezone, new Date(nowMs));
  const errors = validateForm(form, now);
  const quotable = Object.keys(errors).length === 0;
  const shipment = useMemo(() => shipmentFromForm(form, timezone), [form, timezone]);
  const quotes = useQuotes(shopId, shipment, quotable && state.step === "details", quoteDelayMs);
  const options = quotes.options || [OWN_FALLBACK];
  const selected = options.find((option) => option.id === form.choice) || null;
  const setField = (name, value) =>
    setForm((current) => (name === "date" ? withDate(current, value, now) : { ...current, [name]: value }));
  const formKey = shipmentKey(shipment);
  const dirty = isFormDirty(form, base);

  useEffect(() => {
    if (!waiting) return;
    const verdict = reviewAfterRefresh(waiting, {
      shipment,
      choice: form.choice,
      options: quotes.options,
      fresh: quotes.fresh,
      loading: quotes.loading,
      answered: quotes.answered,
      receivedAt: quotes.receivedAt,
      nowMs: Date.now(),
    });
    if (verdict.type === "wait") return;
    setWaiting(null);
    if (verdict.type === "review") dispatch({ type: "review", offer: verdict.offer });
    else if (verdict.type === "changed") setRefreshed(true);
  }, [waiting, shipment, form.choice, quotes.options, quotes.fresh, quotes.loading, quotes.answered, quotes.receivedAt]);

  // "Review again" belongs to the price it was about; any edit replaces that price.
  useEffect(() => setRefreshed(false), [formKey, form.choice]);

  const footer = footerModel({
    step: state.step,
    phase: state.phase,
    option: selected,
    offer: state.offer,
    fresh: quotes.fresh,
    quoting: quotes.loading,
    // An error belongs to the last answer; it says nothing while new prices load or the form is incomplete.
    quoteError: quotable && !quotes.loading ? quotes.error : null,
    errors,
    tried: state.tried,
    secondsLeft: state.offer ? secondsLeft(state.offer, nowMs) : null,
    oldPriceCents: state.oldPriceCents,
    message: state.message,
    reason: state.reason,
    savedRowId: state.savedRowId,
    earlierAttempt: state.earlierAttempt,
    refreshing: !!waiting,
    refreshed,
    busy,
  });

  const jumpTo = (element, focusTarget) => {
    const body = bodyRef.current;
    if (!body || !element) return;
    body.scrollTop = element.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 40;
    if (focusTarget && focusTarget.focus) focusTarget.focus({ preventScroll: true });
  };
  const jumpToCarriers = () => {
    const group = carriersRef.current;
    const checked = group && group.querySelector('[role="radio"][aria-checked="true"]');
    jumpTo(group, checked || group);
  };
  const jumpToProblem = () => {
    const key = firstErrorField(errors);
    const field = key && document.getElementById(`md-field-${key}`);
    if (field) jumpTo(field, field);
    else jumpToCarriers();
  };

  const onReview = () => {
    dispatch({ type: "tried" });
    setRefreshed(false);
    if (!quotable) {
      setTimeout(jumpToProblem, 0);
      return;
    }
    if (!selected) return;
    if (selected.status !== "ok") {
      setTimeout(jumpToCarriers, 0);
      return;
    }
    if (selected.kind === "own") {
      // The store fee is shown only when it was priced for exactly these details; the save never depends on it.
      const option = quotes.fresh ? selected : { ...selected, price_cents: null };
      dispatch({ type: "review", offer: offerFromOption(option, shipment, Date.now()) });
      return;
    }
    if (!quotes.fresh || quotes.loading) return;
    const offer = offerFromOption(selected, shipment, quotes.receivedAt);
    if (!offer) return;
    if (secondsLeft(offer, Date.now()) < REVIEW_MIN_SECONDS) {
      // Never reviewed on this token: the fresh answer opens Review itself, or asks for it again (reviewAfterRefresh).
      setWaiting(reviewWait(selected, shipment, quotes.answered));
      quotes.refresh();
      return;
    }
    dispatch({ type: "review", offer });
  };

  const book = async () => {
    // One clock reading for the hold check and the start: the request goes out only if the reducer will be in
    // booking to receive its answer (an answer it drops could leave a booking staff never see).
    const start = { type: "bookStart", nowMs: Date.now() };
    if (inFlight.current || !canBook(state, start.nowMs)) return;
    if (panelReducer(state, start).phase !== "booking") return;
    inFlight.current = true;
    setBusy(true);
    const { offer } = state;
    const request = bookingRequest(state);
    dispatch(start);
    let result = null;
    try {
      if (request.type === "own") {
        result = await createOwnDriverDelivery(shopId, ownDriverPayload(form, timezone));
      } else if (request.type === "retry") {
        result = await retryManualDeliveryBooking(shopId, request.rowId, request.priceCents);
      } else {
        result = await bookManualDelivery(shopId, request.body);
      }
    } catch (error) {
      result = null;
    }
    // For the own driver an unclear answer means "maybe saved"; it stays unknown so it cannot be sent twice.
    const outcome = bookingOutcome(result);
    inFlight.current = false;
    if (!mounted.current) return;
    setBusy(false);
    if (outcome.kind === "success") {
      const when = `${dayLabel(form.date, todayIso)}, ${windowText(form.start)}`;
      onBooked(successToast(offer, { name: offer.shipment.contact.name || "No name", when }), outcome.row);
      return;
    }
    dispatch({ type: "bookResult", outcome, nowMs: Date.now() });
  };

  const requote = async () => {
    if (busy || !state.offer) return;
    const { offer } = state;
    setBusy(true);
    let result = null;
    try {
      result = await quoteAllOptions(shopId, offer.shipment);
    } catch (error) {
      result = null;
    }
    if (!mounted.current) return;
    setBusy(false);
    const list = result && result.ok && result.data && Array.isArray(result.data.options) ? result.data.options : [];
    const fresh = list.find((option) => option.id === offer.optionId);
    const next = fresh && fresh.status === "ok" ? offerFromOption(fresh, offer.shipment, Date.now()) : null;
    if (next) {
      dispatch({ type: "requoted", offer: next });
      return;
    }
    let message = "We couldn't get a new price";
    if (fresh && fresh.reason) message = fresh.reason;
    else if (result && result.data && result.data.error) message = result.data.error;
    dispatch({ type: "requoteFailed", message });
  };

  const finishClose = () => {
    const rowId = state.savedRowId;
    if (state.phase === "unknown") {
      if (state.offer && state.offer.kind === "own") onSaved("ownUnconfirmed", rowId);
      else onSaved(rowId ? "unknown" : "unconfirmed", rowId);
    } else if (rowId) {
      // Any row the server kept and nobody booked, whatever the last answer was (409, 422, a 401 on its retry).
      onSaved("notBooked", rowId);
    }
    onClose();
  };

  // Edits nobody saved need a yes before they go; the overlay never discards them (a stray click is too easy).
  const requestClose = async ({ fromOverlay = false } = {}) => {
    if (state.phase === "booking") return;
    // The unknown steps must stay readable; only X or Save and close leave them.
    if (fromOverlay && state.phase === "unknown") return;
    const discards = dirty && !state.savedRowId && !NO_DISCARD.includes(state.phase);
    if (discards) {
      if (fromOverlay) return;
      const ok = await confirm({
        title: "Discard this delivery?",
        body: "The details you entered will be lost.",
        action: "Discard",
        cancelLabel: "Keep editing",
      });
      if (!ok || !mounted.current) return;
    }
    finishClose();
  };

  // Escape on the document, so it works wherever focus is. An open alertdialog stops Escape in the capture phase
  // before it gets here.
  const closeRef = useRef(null);
  closeRef.current = requestClose;
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      closeRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const run = (action) => {
    switch (action) {
      case "review":
        return onReview();
      case "close":
        return requestClose();
      case "saveClose":
        return finishClose();
      case "edit":
        return dispatch({ type: "edit" });
      case "book":
        return book();
      case "requote":
        return requote();
      case "openDashboard":
        if (state.offer && state.offer.dashboardUrl) {
          window.open(state.offer.dashboardUrl, "_blank", "noopener,noreferrer");
        }
        return undefined;
      case "jump":
        return jumpToProblem();
      case "jumpCarriers":
        return jumpToCarriers();
      default:
        return undefined;
    }
  };

  const isDetails = state.step === "details";
  return (
    <div ref={layerRef}>
      <div className="md-overlay" onClick={() => requestClose({ fromOverlay: true })} />
      <aside role="dialog" aria-modal="true" aria-labelledby="md-panel-title" className="md-panel">
        <div className="md-panel__head">
          <div className="md-panel__titlebar">
            <h2 id="md-panel-title" ref={titleRef} tabIndex={-1} className="md-panel__title">
              {footer.title}
            </h2>
            <button
              type="button"
              className="md-btn md-btn--icon"
              aria-label="Close"
              disabled={state.phase === "booking"}
              onClick={() => requestClose()}
            >
              ×
            </button>
          </div>
          <ol className="md-steps">
            <li
              className={`md-steps__item ${isDetails ? "md-steps__item--on" : "md-steps__item--done"}`}
              aria-current={isDetails ? "step" : undefined}
            >
              <span className="md-steps__dot" aria-hidden="true">
                1
              </span>
              Details and price
            </li>
            <li className="md-steps__sep" aria-hidden="true" />
            <li
              className={`md-steps__item${isDetails ? "" : " md-steps__item--on"}`}
              aria-current={isDetails ? undefined : "step"}
            >
              <span className="md-steps__dot" aria-hidden="true">
                2
              </span>
              Review and book
            </li>
          </ol>
        </div>
        <div ref={bodyRef} className="md-panel__body">
          {isDetails ? (
            <DeliveryForm
              form={form}
              errors={state.tried ? errors : startError(errors)}
              onField={setField}
              todayIso={todayIso}
              now={now}
              showPickupNotes={!selected || selected.kind !== "own"}
              carriers={
                <CarrierOptions
                  groupRef={carriersRef}
                  options={options}
                  fresh={quotes.fresh}
                  loading={quotes.loading}
                  selectedId={form.choice}
                  onSelect={(id) => setField("choice", id)}
                  onFix={(fix, id) => setForm((current) => applyFix({ ...current, choice: id }, fix))}
                />
              }
            />
          ) : (
            <ReviewStep
              offer={state.offer}
              oldPriceCents={state.oldPriceCents}
              form={form}
              todayIso={todayIso}
              timezone={timezone}
            />
          )}
        </div>
        <PanelFooter model={footer} onAction={run} />
      </aside>
      {confirmElement}
    </div>
  );
}
